import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { UploadCloud, X, FileText, Loader2 } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";

const ACCEPT = ".png,.jpg,.jpeg,.webp,.gif,.pdf";
const MAX_BYTES = 10 * 1024 * 1024;

export default function FileUploadDropzone({ files, setFiles, purpose = "claim_evidence", claimId }) {
  const { t } = useTranslation();
  const inputRef = useRef(null);
  const [active, setActive] = useState(false);
  const [busy, setBusy] = useState(false);

  async function uploadOne(f) {
    if (f.size > MAX_BYTES) {
      toast.error(`${f.name}: > 10MB`);
      return null;
    }
    const fd = new FormData();
    fd.append("file", f);
    const params = new URLSearchParams({ purpose });
    if (claimId) params.append("claim_id", claimId);
    const { data } = await api.post(`/uploads?${params.toString()}`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return { ...data, original_name: f.name };
  }

  async function handleFiles(list) {
    const arr = Array.from(list || []);
    if (!arr.length) return;
    setBusy(true);
    try {
      const uploaded = [];
      for (const f of arr) {
        const r = await uploadOne(f);
        if (r) {
          uploaded.push(r);
          if (r.duplicate_of) {
            toast.warning(`${f.name}: duplicate of a previously-uploaded file (hash match)`, {
              description: "Underwriters may flag this — submit fresh evidence if possible.",
            });
          }
        }
      }
      setFiles([...(files || []), ...uploaded]);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  }

  function remove(id) {
    setFiles((files || []).filter((x) => x.id !== id));
  }

  return (
    <div data-testid="file-upload-dropzone">
      <div
        onDragOver={(e) => { e.preventDefault(); setActive(true); }}
        onDragLeave={() => setActive(false)}
        onDrop={(e) => {
          e.preventDefault(); setActive(false);
          handleFiles(e.dataTransfer.files);
        }}
        onClick={() => inputRef.current?.click()}
        data-active={active}
        className="dropzone p-6 cursor-pointer flex flex-col items-center justify-center text-center"
      >
        {busy ? (
          <>
            <Loader2 className="w-6 h-6 text-[#114232] animate-spin" />
            <div className="text-sm text-[#5c6360] mt-2">{t("file_claim.uploading")}</div>
          </>
        ) : (
          <>
            <UploadCloud className="w-7 h-7 text-[#114232]" strokeWidth={1.5} />
            <div className="text-sm text-[#1b1d1c] mt-2 font-medium">{t("file_claim.drop_or_click")}</div>
            <div className="text-xs text-[#5c6360] mt-1">{t("file_claim.evidence_help")}</div>
          </>
        )}
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPT}
          multiple
          className="hidden"
          data-testid="file-upload-input"
          onChange={(e) => handleFiles(e.target.files)}
        />
      </div>
      {(files || []).length > 0 && (
        <ul className="mt-3 space-y-1.5" data-testid="upload-list">
          {files.map((f) => (
            <li
              key={f.id}
              className="flex items-center justify-between bg-white border border-[#114232]/10 rounded-md px-3 py-2 text-sm"
              data-testid={`upload-item-${f.id}`}
            >
              <span className="flex items-center gap-2 truncate">
                <FileText className="w-4 h-4 text-[#114232] shrink-0" />
                <span className="truncate">{f.original_name || f.filename}</span>
                <span className="text-[10px] text-[#5c6360] font-mono shrink-0">
                  {(f.size_bytes / 1024).toFixed(1)}KB
                </span>
              </span>
              <button
                type="button"
                onClick={() => remove(f.id)}
                data-testid={`upload-remove-${f.id}`}
                className="text-[#5c6360] hover:text-[#c15337] transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
